// Loosely based on Mantine 7.9+ Accordion component

import { useMemo } from "react";
import { useUncontrolled } from "../../common/hooks/useUncontrolled";
import { AccordionProvider } from "./Context";

type AccordionProps = {
    children: React.ReactNode;
    value?: string;
    defaultValue?: string;
    onChange?: (value: string) => void;
    multiple?: boolean;
};

export default function Accordion({
    children,
    value,
    defaultValue,
    onChange,
    multiple = false,
}: AccordionProps) {
    const _id = useMemo(
        () => `accordion-${Math.random().toString(36).substr(2, 9)}`,
        [],
    );

    const [_value, handleChange] = useUncontrolled({
        value,
        defaultValue,
        finalValue: multiple ? ([] as any) : undefined,
        onChange,
    });

    const isItemActive = (id: string) => {
        if (Array.isArray(_value)) {
            return _value.includes(id);
        }
        return _value === id;
    };

    const handleItemChange = (id: string) => {
        const nextValue = () => {
            if (Array.isArray(_value)) {
                if (_value.includes(id)) {
                    return _value.filter((current) => current !== id);
                }
                return [..._value, id];
            }
            if (_value === id) {
                return undefined;
            }
            return id;
        };
        handleChange(nextValue() as string);
    };

    return (
        <AccordionProvider
            value={{
                isItemActive,
                onChange: handleItemChange,
                getControlId: (id: string) => `${id}-${_id}-control`,
                getRegionId: (id: string) => `${id}-${_id}-panel`,
            }}
        >
            {children}
        </AccordionProvider>
    );
}
